/* eslint-disable @next/next/no-img-element */
import Link from 'next/link';
import { MatchDetailItemFragment, TeamListItemFragment } from '@amf/shared/types/graphql';
import { useTranslate } from '@tolgee/react';
import React from 'react';
import TeamDress from '@amf/shared/components/team/TeamDress';
import { fromUnixTime, isFuture, isPast } from 'date-fns';
import { useRouter } from 'next/router';
import { formatDate, getDateLocale } from '@amf/shared/utils/date';
import Container from '@amf/shared/components/layout/Container';
import Button from '@amf/shared/components/button/Button';
import IconArrowLeft from '@amf/shared/icons/IconArrowLeft';
import Pulser from '@amf/shared/components/utils/Pulser';
import { isUsingSystemMatchEvents } from '@amf/shared/data/Match';
import MatchJumbotronTimer from '@amf/shared/components/match/MatchJumbotronTimer';
import MatchJumbotronStatus from '@amf/shared/components/match/MatchJumbotronStatus';

type MatchHeaderTeamProps = {
  team?: TeamListItemFragment;
  prefixes: { team: string };
  home?: boolean;
};

function MatchHeaderTeam({ team, prefixes, home }: MatchHeaderTeamProps) {
  const { t } = useTranslate();
  if (!team) {
    return (
      <div className='MatchHeaderAlt__team'>
        <div className='MatchHeaderAlt__teamContent'>
          <div className='MatchHeaderAlt__teamImage'>?</div>
          <div className='MatchHeaderAlt__teamName'>{t('match.header.team.unknown')}</div>
        </div>
      </div>
    );
  }

  return (
    <Link href={`${prefixes.team}/${team.id}`}>
      <a className='MatchHeaderAlt__team'>
        <div className='MatchHeaderAlt__teamContent'>
          {team.club.brandUrl && (
            <img
              className='MatchHeaderAlt__teamImage'
              src={team.club.brandUrl}
              alt={team.club.name}
            />
          )}
          {!team.club.brandUrl && (
            <div className='MatchHeaderAlt__teamImage'>{team.name.charAt(0)}</div>
          )}
          <div className='MatchHeaderAlt__teamName'>{team.name}</div>
          <div className='MatchHeaderAlt__teamClub'>
            {home ? t('match.roster.home') : t('match.roster.visiting')}
          </div>
          <TeamDress className='MatchHeaderAlt__teamDress' dress={team} away={!home} />
        </div>
        <div className='MatchHeaderAlt__teamCrosses'>
          <div className='MatchHeaderAlt__teamCross' />
          <div className='MatchHeaderAlt__teamCross' />
          <div className='MatchHeaderAlt__teamCross' />
          <div className='MatchHeaderAlt__teamCross' />
          <div className='MatchHeaderAlt__teamCross' />
        </div>
      </a>
    </Link>
  );
}

type MatchHeaderAboutProps = {
  match: MatchDetailItemFragment;
  prefixes: { referee: string };
};

function MatchHeaderAbout({ match, prefixes }: MatchHeaderAboutProps) {
  const { t } = useTranslate();
  const { locale } = useRouter();
  const dateLocale = getDateLocale(locale);
  const start = fromUnixTime(match.dateStart);
  const end = match.dateEnd ? fromUnixTime(match.dateEnd) : null;
  const mainReferee = match.referees.find(r => r.role === 'main');
  const assistReferees = match.referees.filter(r => r.role === 'assistant');

  const referees = (
    <>
      {(mainReferee || assistReferees.length > 0) && (
        <div className='MatchHeaderAlt__aboutOther'>
          <div className='MatchHeaderAlt__aboutOtherTitle'>
            <i className='icons-whistle' /> {t('match.header.referees')}
          </div>
          {mainReferee && (
            <div className='MatchHeaderAlt__aboutReferee'>
              <Link href={`${prefixes.referee}/${mainReferee.referee.id}`} passHref>
                <a>
                  {mainReferee.referee.fullName} <small>(H)</small>
                </a>
              </Link>
            </div>
          )}
          {assistReferees.map(referee => (
            <div key={referee.id} className='MatchHeaderAlt__aboutReferee'>
              <Link href={`${prefixes.referee}/${referee.referee.id}`} passHref>
                <a>
                  {referee.referee.fullName} <small>(A)</small>
                </a>
              </Link>
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (isUsingSystemMatchEvents(match) && match.live) {
    return (
      <div className='MatchHeaderAlt__about'>
        <div className='MatchHeaderAlt__aboutScore'>{match.score}</div>
        <div className='MatchHeaderAlt__aboutHalfTime'>
          <MatchJumbotronStatus match={match} />
          {!match.dateEnd && <MatchJumbotronTimer match={match} />}
        </div>
        {referees}
        {!match.dateEnd && (
          <div className='MatchHeaderAlt__aboutNow'>
            <Pulser /> {t('match.header.live')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='MatchHeaderAlt__about'>
      {isFuture(start) ? (
        <>
          <div className='MatchHeaderAlt__aboutScore MatchHeaderAlt__aboutScore--small'>
            {formatDate(start, 'EEEEEE HH:mm', { locale: dateLocale })}
          </div>
          <div className='MatchHeaderAlt__aboutHalfTime'>
            {formatDate(start, 'dd/MM/yy', { locale: dateLocale })}
          </div>
        </>
      ) : (
        <>
          <div className='MatchHeaderAlt__aboutScore'>{match.score}</div>
          {match.status && match.live && (
            <div className='MatchHeaderAlt__aboutHalfTime'>
              {t(`match.event.status.${match.status}`)}
            </div>
          )}
        </>
      )}
      {isPast(start) && end === null && match.live && match.started && (
        <div className='MatchHeaderAlt__aboutNow'>
          <Pulser /> {t('match.header.live')}
        </div>
      )}
      {referees}
    </div>
  );
}

type MatchHeaderAltProps = {
  match: MatchDetailItemFragment;
  prefixes: { team: string; matches: string; ground?: string; referee: string };
};

function MatchHeaderAlt({ match, prefixes }: MatchHeaderAltProps) {
  const { t } = useTranslate();
  const start = fromUnixTime(match.dateStart);
  const mainReferee = match.referees.find(r => r.role === 'main');
  const assistReferees = match.referees.filter(r => r.role === 'assistant');
  return (
    <div className='MatchHeaderAlt__layout'>
      <Container>
        <div className='MatchHeaderAlt__navigation'>
          <h1>{t('match.header.title')}</h1>
          <Link href={prefixes.matches}>
            <a>
              <Button variant='dark-outline' leftIcon={<IconArrowLeft />}>
                {t('match.header.back')}
              </Button>
            </a>
          </Link>
        </div>
        <div className='MatchHeaderAlt'>
          <div className='MatchHeaderAlt__header'>
            <div className='MatchHeaderAlt__headerTime'>
              <span>{formatDate(start, 'dd.MM.yyyy')}</span>
              <span>{formatDate(start, 'HH:mm')}</span>
            </div>
            <div className='MatchHeaderAlt__headerLeague'>
              <span className='MatchHeaderAlt__headerLeagueName'>{match.leagueName}</span>
              <span className='MatchHeaderAlt__headerLeaguePhase'>
                {match.phaseName} ({t('match.toolbar.round.value', { round: match.round })})
              </span>
            </div>
            <div>
              {match.ground && prefixes.ground && (
                <div className='MatchHeaderAlt__headerGround'>
                  <Link href={`${prefixes.ground}/${match.ground.id}`}>
                    <a>
                      <span className='MatchHeaderAlt__headerGroundTitle'>
                        {t('match.header.ground')}
                      </span>
                      <span>
                        <i className='icons-stadium' /> {match.ground.name}
                      </span>
                    </a>
                  </Link>
                </div>
              )}
              {match.ground && !prefixes.ground && (
                <div className='MatchHeaderAlt__headerGround'>
                  <div>
                    <span className='MatchHeaderAlt__headerGroundTitle'>
                      {t('match.header.ground')}
                    </span>
                    <span>
                      <i className='icons-stadium' /> {match.ground.name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='MatchHeaderAlt__content'>
            <MatchHeaderTeam prefixes={prefixes} team={match.home?.team} home />
            <MatchHeaderAbout prefixes={prefixes} match={match} />
            <MatchHeaderTeam prefixes={prefixes} team={match.visiting?.team} />
          </div>
          {(mainReferee || assistReferees.length > 0) && (
            <div className='MatchHeaderAlt__referees'>
              <div className='MatchHeaderAlt__aboutOtherTitle'>
                <i className='icons-whistle' /> {t('match.header.referees')}
              </div>
              {mainReferee && (
                <div className='MatchHeaderAlt__aboutReferee'>
                  <Link href={`${prefixes.referee}/${mainReferee.referee.id}`} passHref>
                    <a>
                      {mainReferee.referee.fullName} <small>(H)</small>
                    </a>
                  </Link>
                </div>
              )}
              {assistReferees.map(referee => (
                <div key={referee.id} className='MatchHeaderAlt__aboutReferee'>
                  <Link href={`${prefixes.referee}/${referee.referee.id}`} passHref>
                    <a>
                      {referee.referee.fullName} <small>(A)</small>
                    </a>
                  </Link>
                </div>
              ))}
            </div>
          )}
          {match.liveStreamEnabled && (
            <>
              <div className='MatchHeaderAlt__ctaHelper' />
              <div className='MatchHeaderAlt__cta'>
                {match.liveStreamURL && (
                  <a href={match.liveStreamURL} target='_blank' rel='noreferrer'>
                    <button className='MatchHeaderAlt__buttonLive'>
                      <i className='bi bi-tv-fill' /> {t('match.toolbar.liveStream.play')}
                    </button>
                  </a>
                )}
                {!match.liveStreamURL && (
                  <button disabled className='MatchHeaderAlt__buttonLive'>
                    <i className='bi bi-tv-fill' /> {t('match.toolbar.liveStream.soon')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
}

export default MatchHeaderAlt;
